import { useEffect, useState, useCallback, useRef } from "react";

const SEND_FLOW_STORAGE_KEY = "send_flow_state";
const EXPIRATION_TIME = 60 * 60 * 1000;

const isStateExpired = (timestamp) => {
  if (!timestamp) return true;
  const currentTime = Date.now();
  return currentTime - timestamp > EXPIRATION_TIME;
};

export const useSendFlowState = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  const clearStateRef = useRef(() => {
    try {
      if (typeof window === "undefined") return;
      sessionStorage.removeItem(SEND_FLOW_STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing send flow state:", error);
    }
  });

  const loadState = useCallback(() => {
    try {
      if (typeof window === "undefined") return null;

      const stored = sessionStorage.getItem(SEND_FLOW_STORAGE_KEY);
      if (!stored) return null;

      const parsed = JSON.parse(stored);
      const { timestamp } = parsed;

      if (isStateExpired(timestamp)) {
        clearStateRef.current();
        return null;
      }

      return parsed.data;
    } catch (error) {
      console.error("Error loading send flow state:", error);
      clearStateRef.current();
      return null;
    }
  }, []);

  const saveState = useCallback((stateData) => {
    try {
      if (typeof window === "undefined") return;

      const dataToStore = {
        timestamp: Date.now(),
        data: stateData,
      };

      sessionStorage.setItem(
        SEND_FLOW_STORAGE_KEY,
        JSON.stringify(dataToStore)
      );
    } catch (error) {
      console.error("Error saving send flow state:", error);
    }
  }, []);

  const clearState = useCallback(() => {
    try {
      if (typeof window === "undefined") return;
      sessionStorage.removeItem(SEND_FLOW_STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing send flow state:", error);
    }
  }, []);

  clearStateRef.current = clearState;

  const updateState = useCallback(
    (updates) => {
      try {
        const currentState = loadState() || {};
        const newState = { ...currentState, ...updates };
        saveState(newState);
        return newState;
      } catch (error) {
        console.error("Error updating send flow state:", error);
        return null;
      }
    },
    [loadState, saveState]
  );

  const getRemainingTime = useCallback(() => {
    try {
      const stored = sessionStorage.getItem(SEND_FLOW_STORAGE_KEY);
      if (!stored) return 0;

      const parsed = JSON.parse(stored);
      const { timestamp } = parsed;

      const remaining = EXPIRATION_TIME - (Date.now() - timestamp);
      return remaining > 0 ? remaining : 0;
    } catch (error) {
      console.error("Error getting remaining time:", error);
      return 0;
    }
  }, []);

  const resetToStep = useCallback(
    (step = "send") => {
      const initialState = {
        currentStep: step,
        selectedAsset: null,
        amount: null,
        zarAmount: null,
        destinationAddress: "",
        selectedNetwork: null,
        tagNumber: null,
        quoteData: null,
      };
      saveState(initialState);
      return initialState;
    },
    [saveState]
  );

  useEffect(() => {
    setIsHydrated(true);

    sessionStorage.setItem("SEND_FLOW_APP_ACTIVE", "true");

    const handlePageHide = () => {
      const isReload =
        performance.getEntriesByType("navigation")[0].type === "reload";

      if (!isReload) {
        clearState();
        sessionStorage.removeItem("SEND_FLOW_APP_ACTIVE");
      }
    };

    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, []);

  return {
    loadState,
    saveState,
    clearState,
    updateState,
    getRemainingTime,
    resetToStep,
    isHydrated,
  };
};
